import { useControllableState } from '../../../utils/useControllableState'
import { DateRangePickerProps, getDatePickerTriggerProps } from '../DatePicker.types'
import { DatePickerCalendar } from '../DatePickerCalendar'
import { useDatePickerOpenState } from '../useDatePickerOpenState'
import { normalizeRangeDateChange } from './normalizeRangeDateChange'

export function DateRangePicker(props: DateRangePickerProps) {
  const {
    className,
    label,
    helpText,
    status = 'default',
    feedbackText,
    placeholder,
    disabled,
    defaultOpen = false,
    defaultStartDate = null,
    defaultEndDate = null,
    startDate,
    endDate,
    minDate,
    maxDate,
    excludeDates,
    includeDates,
    locale = 'en-GB',
    dateFormat = 'dd.MM.yyyy',
    nextMonthAriaLabel = 'Go to next month',
    previousMonthAriaLabel = 'Go to previous month',
    showWeekNumbers,
    weekLabel = 'Week',
    monthsShown = 1,
    inline = false,
    onChange,
  } = props
  const [start, setStart] = useControllableState<Date | null>(startDate, defaultStartDate)
  const [end, setEnd] = useControllableState<Date | null>(endDate, defaultEndDate)
  const { isOpen, handleOpen, handleClose, handleKeyDown } = useDatePickerOpenState(defaultOpen, disabled, inline)

  const handleDateChange = (value: Date | [Date | null, Date | null] | null) => {
    const normalizedRange = normalizeRangeDateChange(value)
    if (!normalizedRange) {
      onChange?.(null)
      return
    }

    const { startDate: nextStartDate, endDate: nextEndDate, shouldClose } = normalizedRange

    setStart(nextStartDate)
    setEnd(nextEndDate)
    onChange?.([nextStartDate, nextEndDate])

    if (shouldClose) {
      handleClose()
    }
  }

  return (
    <DatePickerCalendar
      className={className}
      label={label}
      helpText={helpText}
      status={status}
      feedbackText={feedbackText}
      placeholder={placeholder}
      disabled={disabled}
      isOpen={isOpen}
      minDate={minDate}
      maxDate={maxDate}
      excludeDates={excludeDates}
      includeDates={includeDates}
      {...getDatePickerTriggerProps(props)}
      locale={locale}
      dateFormat={dateFormat}
      nextMonthAriaLabel={nextMonthAriaLabel}
      previousMonthAriaLabel={previousMonthAriaLabel}
      shouldCloseOnSelect={false}
      selectsRange
      selected={start}
      startDate={start}
      endDate={end}
      onChange={handleDateChange}
      onOpen={handleOpen}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      showWeekNumbers={showWeekNumbers}
      weekLabel={weekLabel}
      monthsShown={monthsShown}
      inline={inline}
    />
  )
}
